import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { Observable, take } from 'rxjs';

import { TwcWizardComponent } from '@topcon/twc-angular';

import { ImportWizardType, WizardCommunicationService } from '@mirage-projects/cw-commons';
import { PcvizJobAdapter } from '@mirage-projects/cw-models';

import { WizardFormatDefinition } from '../../models/wizard-format-option';
import { CreateProjectService } from '../../services/create-project.service';
import { UploadWizardService } from '../../services/upload-wizard-service/upload-wizard.service';
import { WorkflowWizardService } from '../../services/workflow/workflow-wizard.service';
import { WizardExtraPanels } from './create-wizard-input-data/wizard-extra-panels';
import { WizardPanelTypes } from './create-wizard-input-data/wizard-panel-types';

/**
 * Create layer/project main component
 * It has the wizard component and orchestrates the creation of the project/layer
 */
@Component({
    selector: 'cw-create-wizard',
    templateUrl: './create-wizard.component.html',
    styleUrls: ['./create-wizard.component.scss'],
    standalone: false,
})
export class CreateWizardComponent implements OnInit, AfterViewInit, OnDestroy {
    /** The job site into which the created project will be added */
    @Input() jobSiteId: number | undefined;

    extraPanel = this.uploadWizardService.extraPanel;
    formatDefinitions: Observable<WizardFormatDefinition[]> | undefined;

    @ViewChild(TwcWizardComponent) wizard!: TwcWizardComponent;

    constructor(
        public uploadWizardService: UploadWizardService,
        public createProjectService: CreateProjectService,
        public workflowWizardService: WorkflowWizardService,
        private wizardCommunicationService: WizardCommunicationService
    ) {}

    ngOnInit(): void {
        this.formatDefinitions = this.uploadWizardService.formatsDefinition;

        this.workflowWizardService.closeWizardWorkflow$.pipe(take(1)).subscribe(() => this.closeWizard());
    }

    ngOnDestroy(): void {
        this.uploadWizardService.showWizard = false;
        this.wizardCommunicationService.uploadWizardClose();
    }

    ngAfterViewInit(): void {
        this.uploadWizardService.init(this.wizard);
        this.uploadWizardService.jobSiteId = this.jobSiteId;
        this.wizard.options ??= {};
        this.wizard.options.data ??= { importType: 'project' };
        this.uploadWizardService.stepChanged = this.wizard.stepChanged;
    }

    nextStep(): void {
        this.uploadWizardService.initStepWizard(this.wizard.selectedIndex);
        this.wizard.nextStep();
    }

    updateExtraSteps(jobAdapter?: PcvizJobAdapter | undefined): void {
        // TODO: check how to use this method without having to use settimeout
        setTimeout(() => {
            if (jobAdapter) {
                const extraPanelName = WizardExtraPanels.get(jobAdapter.getId());
                const canUseExtraPanels: boolean =
                    typeof jobAdapter.canUseExtraPanels === 'function' ? jobAdapter.canUseExtraPanels() : true;

                this.extraPanel.panelName = canUseExtraPanels ? extraPanelName : undefined;
            }
        });
    }

    /* istanbul ignore next*/
    onPrevButtonClick(): void {
        this.wizard.prevStep();
    }

    /* istanbul ignore next*/
    onNextButtonClick(): void {
        this.nextStep();
    }

    /* istanbul ignore next*/
    onUploadButtonClick(): void {
        this.uploadWizardService.uploadAndProcess(this.jobSiteId);
    }

    /* istanbul ignore next*/
    onCloseButtonClick(): void {
        this.closeWizard();
    }

    onNameChanged(isValid: boolean): void {
        this.uploadWizardService.isWorkspaceValid = isValid;
    }

    hasToShowScanProcessingStep(): boolean {
        const wizardType: ImportWizardType[] = ['CreateStation', 'ImportStation', 'UpgradeSparse', 'ImportProject'];
        return this.isCurrentTypeAndShowWizard(wizardType);
    }

    hasToShowCRP1SelectScans(): boolean {
        const wizardType: ImportWizardType[] = ['ImportStation'];
        return this.isCurrentTypeAndShowWizard(wizardType) && this.uploadWizardService.isCrp1;
    }

    hasToShowDestinationGroupStep(): boolean {
        const wizardType: ImportWizardType[] = ['ImportRefs'];
        return this.isCurrentTypeAndShowWizard(wizardType);
    }

    hasToShowPointSelectorStep(): boolean {
        const wizardType: ImportWizardType[] = ['CreateStation'];
        return this.isCurrentTypeAndShowWizard(wizardType);
    }

    get title(): string {
        const resource = 'UPLOAD.TITLE';
        return this.uploadWizardService.selectInitialJob ? `${resource}_${this.uploadWizardService.importType}` : resource;
    }

    get wizardPanelTypes(): typeof WizardPanelTypes {
        return WizardPanelTypes;
    }

    get isPrevButtonVisible(): boolean {
        return !this.uploadWizardService.startedUpload;
    }

    /* istanbul ignore next*/
    get isFirstStep(): boolean {
        return this.wizard?.isFirstStep;
    }

    /* istanbul ignore next*/
    get isUploadStarted(): boolean {
        return this.uploadWizardService.startedUpload;
    }

    get isNextButtonVisible(): boolean {
        return !this.wizard?.isLastStep;
    }

    get isNextButtonEnabled(): boolean {
        return this.uploadWizardService.isValidStep();
    }

    get isUploadButtonVisible(): boolean {
        return this.wizard?.isLastStep && !this.uploadWizardService.startedUpload;
    }

    get isCloseButtonVisible(): boolean {
        return this.uploadWizardService.startedUpload;
    }

    private isCurrentTypeAndShowWizard(wizardType: string[]): boolean {
        return wizardType.includes(this.uploadWizardService.importType as any) && this.uploadWizardService.showWizard;
    }

    private closeWizard(): void {
        this.wizard.closable = true;
        this.wizard.close.emit();
    }
}
